import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';

import { IProgram } from 'shared/interfaces/IProgram';
import {
  IProgramSetup,
  IProgramSetupRequestPayload,
  IProgramSetupResponsePayload,
} from 'shared/interfaces/IProgramSetup';
import { useProgramSetupService } from 'shared/services/programSetupService';
import { Alert } from 'shared/theme/elements';
import { programSetupRecordToForm } from 'shared/utils/programSetupRecordToForm';
import { ConflictList } from './ConflictList';
import { Empty, Guide } from './ProgramSetupSchedule.styled';
import { PendingChange, ScheduleChangeModal } from './ScheduleChangeModal';
import { ScheduleGuide } from './ScheduleGuide';
import {
  airingDaysOf,
  applyChange,
  ChangeScope,
  DAY_NAMES,
  fmtRange,
  nameOf,
  overlapPairs,
  ScheduleChange,
  ScheduleMode,
} from './scheduleUtils';

// The guide needs every program on the channel at once, not the table's current page.
const SCHEDULE_LIMIT = 500;

const schedulePayload = (mode: ScheduleMode, channelId?: string): IProgramSetupRequestPayload | undefined => {
  if (!channelId) return undefined;
  return mode === 'tv'
    ? { page: 1, limit: SCHEDULE_LIMIT, sort: '', channel_obj_id: channelId, audio_setup: null }
    : { page: 1, limit: SCHEDULE_LIMIT, sort: '', station_obj_id: channelId, audio_setup: 'yes' };
};

/** Every program setup on one channel (TV) or station (Radio). */
export const useScheduleRecords = (mode: ScheduleMode, channelId?: string) => {
  const { getProgramSetup } = useProgramSetupService();
  return getProgramSetup(schedulePayload(mode, channelId));
};

/** Number of overlapping pairs on the channel/station — shares the guide's query, so no extra request. */
export const useChannelConflicts = (mode: ScheduleMode, channelId?: string) => {
  const { data } = useScheduleRecords(mode, channelId);
  return useMemo(() => overlapPairs(data?.data ?? []).length, [data?.data]);
};

interface Props {
  mode: ScheduleMode;
  channelId?: string;
  programById: Record<string, IProgram>;
  onEdit: (id: string) => void;
}

const ProgramSetupScheduleContainer = ({ mode, channelId, programById, onEdit }: Props) => {
  const user = localStorage?.getItem('user_admin') ? JSON.parse(localStorage.getItem('user_admin') || '') : null;
  const queryClient = useQueryClient();

  const { data, isLoading, isFetching } = useScheduleRecords(mode, channelId);
  const records = useMemo(() => data?.data ?? [], [data?.data]);
  const pairs = useMemo(() => overlapPairs(records), [records]);

  const { updateProgramSetup } = useProgramSetupService();
  const { updateMutation } = updateProgramSetup();

  const [pending, setPending] = useState<PendingChange | null>(null);
  const [resetSignal, setResetSignal] = useState(0);

  const handlePropose = useCallback((record: IProgramSetup, fromDay: number, next: ScheduleChange) => {
    setPending({ record, fromDay, next });
  }, []);

  const closeModal = useCallback(() => {
    setPending(null);
    setResetSignal((n) => n + 1);
  }, []);

  const handleApply = (scope: ChangeScope) => {
    if (!pending) return;
    const { record, fromDay, next } = pending;
    const dailySchedule = applyChange(record.daily_schedule, fromDay, next, scope);
    const updated: IProgramSetup = { ...record, daily_schedule: dailySchedule };
    const base = programSetupRecordToForm(updated, mode);
    const allDays = scope === 'all' && next.day === fromDay;

    updateMutation.mutate(
      {
        data: {
          ...base,
          _id: record._id,
          updated_by: user?._id,
          ...(mode === 'radio' && base.recording_title_setup
            ? {
                recording_title_setup: {
                  ...base.recording_title_setup,
                  ocr_lang: base.recording_title_setup.ocr_lang || 'eng',
                },
              }
            : {}),
        },
        image: {},
      },
      {
        onSuccess: () => {
          // Show the new time straight away; the refetch below confirms it.
          queryClient.setQueryData<IProgramSetupResponsePayload | undefined>(
            ['program_setup', schedulePayload(mode, channelId)],
            (old) => (old ? { ...old, data: old.data.map((r) => (r._id === record._id ? updated : r)) } : old),
          );
          const count = airingDaysOf(dailySchedule).length;
          Alert.success(
            'Schedule Updated!',
            allDays
              ? `${nameOf(record, mode)} · all ${count} occurrence${count === 1 ? '' : 's'} now ${fmtRange(next)}`
              : `${nameOf(record, mode)} · ${DAY_NAMES[next.day]} only · ${fmtRange(next)}`,
          );
          closeModal();
          queryClient.invalidateQueries('program_setup');
        },
        onError: (error: any) => {
          Alert.error(error?.response?.data?.server_response || 'Something went wrong');
          closeModal();
        },
      },
    );
  };

  if (!channelId) {
    return (
      <Guide>
        <Empty>Select a {mode === 'tv' ? 'channel' : 'station'} to see its weekly schedule.</Empty>
      </Guide>
    );
  }

  return (
    <>
      <ScheduleGuide
        mode={mode}
        records={records}
        pairs={pairs}
        programById={programById}
        loading={isLoading || (isFetching && !records.length)}
        resetSignal={resetSignal}
        onPropose={handlePropose}
        onEdit={onEdit}
      />
      <ConflictList mode={mode} records={records} pairs={pairs} onEdit={onEdit} />
      <ScheduleChangeModal
        mode={mode}
        records={records}
        pending={pending}
        saving={updateMutation.isLoading}
        onCancel={closeModal}
        onApply={handleApply}
      />
    </>
  );
};

export default ProgramSetupScheduleContainer;
